import React, { useEffect } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Archive, Trash2, Edit, Plus, FileText } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { EmptyState } from "@/components/EmptyState";
import { useUser } from "@/hooks/use-user";
import { useNavigate } from "react-router-dom";

interface ContentItem {
  id: string;
  title: string;
  image_url?: string;
  cover_image?: string;
  cover_url?: string;
  created_at: string;
  archived: boolean;
  category?: string;
}

interface ContentManagementGridProps {
  items: ContentItem[];
  contentType: 'pin' | 'webtoon' | 'webnovel';
  onArchive: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onEdit: (id: string) => void;
  onAddChapter?: (id: string) => void;
  refreshData: () => void;
  isLoading?: boolean;
}

export const ContentManagementGrid = ({
  items,
  contentType,
  onArchive,
  onDelete,
  onEdit,
  onAddChapter,
  refreshData,
  isLoading = false,
}: ContentManagementGridProps) => {
  const { user, isLoading: userLoading } = useUser();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!userLoading && !user) {
      toast.error("Você precisa estar logado para gerenciar conteúdo");
      navigate('/auth');
    }
  }, [user, userLoading, navigate]);
  
  useEffect(() => {
    // Debug log para verificar se os itens estão sendo recebidos
    console.log(`ContentManagementGrid recebeu ${items?.length || 0} ${contentType}s:`, items);
  }, [items, contentType]);
  
  const handleAction = async (action: 'archive' | 'delete' | 'edit' | 'add-chapter', id: string) => {
    // Check if user is logged in before allowing actions
    if (!user) {
      toast.error("Você precisa estar logado para gerenciar conteúdo");
      navigate('/auth');
      return;
    }
    
    try {
      switch (action) {
        case 'archive':
          await onArchive(id);
          break;
        case 'delete':
          await onDelete(id);
          break;
        case 'edit':
          onEdit(id);
          break;
        case 'add-chapter':
          if (onAddChapter) {
            onAddChapter(id);
          }
          break;
      }
      refreshData();
    } catch (error) {
      console.error(`Erro ao executar ${action}:`, error);
      toast.error(`Erro ao ${action === 'archive' ? 'arquivar' : action === 'delete' ? 'excluir' : 'editar'} o ${contentType}`);
    }
  };

  const getImageUrl = (item: ContentItem) => {
    if (item.image_url) return item.image_url;
    if (item.cover_image) return item.cover_image;
    if (item.cover_url) return item.cover_url;
    return "https://via.placeholder.com/150";
  };

  const formatContentType = (type: string) => {
    switch (type) {
      case 'pin': return 'Pin';
      case 'webtoon': return 'Webtoon';
      case 'webnovel': return 'Webnovel';
      default: return type;
    }
  };

  const getCreatePath = () => {
    switch (contentType) {
      case 'pin': return '/create/pin';
      case 'webtoon': return '/create/webtoon';
      case 'webnovel': return '/create/webnovel';
      default: return '/create';
    }
  };

  const getEmptyStateProps = () => {
    switch (contentType) {
      case 'pin':
        return {
          title: "Nenhum Pin encontrado",
          description: "Você ainda não criou nenhum Pin. Que tal criar seu primeiro Ink Pin agora?",
          actionLabel: "Criar novo Pin",
          actionPath: "/create/pin"
        };
      case 'webtoon':
        return {
          title: "Nenhum Webtoon encontrado",
          description: "Você ainda não criou nenhum Webtoon. Comece sua história em quadrinhos agora!",
          actionLabel: "Criar novo Webtoon",
          actionPath: "/create/webtoon"
        };
      case 'webnovel':
        return {
          title: "Nenhuma Webnovel encontrada",
          description: "Você ainda não criou nenhuma Webnovel. Comece a escrever sua história agora!",
          actionLabel: "Criar nova Webnovel",
          actionPath: "/create/webnovel"
        };
      default:
        return {
          title: "Nenhum conteúdo encontrado",
          description: "Você ainda não criou nenhum conteúdo.",
          actionLabel: "Criar novo",
          actionPath: "/create"
        };
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Log de debug para verificar o array de itens
  console.log("Renderizando ContentManagementGrid com itens:", items);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Seus {formatContentType(contentType)}s</h2>
        <Button onClick={() => {
          if (!user) {
            toast.error("Você precisa estar logado para criar conteúdo");
            navigate('/auth');
            return;
          }
          window.location.href = getCreatePath()
        }}>
          <Plus className="mr-2 h-4 w-4" />
          Criar Novo
        </Button>
      </div>
      
      {!items || items.length === 0 ? (
        <EmptyState {...getEmptyStateProps()} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <div className="aspect-square overflow-hidden relative">
                <img
                  src={getImageUrl(item)}
                  alt={item.title}
                  className="object-cover w-full h-full"
                />
                {item.archived && (
                  <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">
                    Arquivado
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-medium truncate">{item.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Criado em {new Date(item.created_at).toLocaleDateString()}
                </p>
                {item.category && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Categoria: {item.category}
                  </p>
                )}
              </CardContent>
              <CardFooter className="flex justify-between p-4 pt-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAction('edit', item.id)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Editar
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleAction('archive', item.id)}>
                      <Archive className="mr-2 h-4 w-4" />
                      {item.archived ? 'Desarquivar' : 'Arquivar'}
                    </DropdownMenuItem>
                    
                    {(contentType === 'webtoon' || contentType === 'webnovel') && onAddChapter && (
                      <DropdownMenuItem onClick={() => handleAction('add-chapter', item.id)}>
                        <FileText className="mr-2 h-4 w-4" />
                        Adicionar Capítulo
                      </DropdownMenuItem>
                    )}
                    
                    <DropdownMenuItem 
                      className="text-destructive focus:text-destructive"
                      onClick={() => {
                        if (window.confirm(`Tem certeza que deseja excluir este ${formatContentType(contentType)}?`)) {
                          handleAction('delete', item.id);
                        }
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Excluir
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
